import { useId, useState } from 'react'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'

/** The form's collected values, handed up to the caller which persists them
 * (`POST /account/addresses`) and shows the new address selected on the cart. */
export interface NewAddressInput {
  readonly label: string
  readonly contactName: string
  readonly companyName: string
  readonly line1: string
  readonly line2: string
  readonly city: string
  readonly region: string
  readonly postalCode: string
  readonly isDefault: boolean
}

export interface AddAddressModalProps {
  readonly open: boolean
  readonly onClose: () => void
  /** Persists the new address and selects it on the cart. Async so the modal
   * can show a busy state and keep itself open if the request fails
   * (rejects). Resolves once the address is saved + selected. */
  readonly onSave: (input: NewAddressInput) => Promise<void>
}

/**
 * The Cart's "Add new address" popup, opened from the Ship-to select's
 * "＋ Add new address…" option (matching the rendered design's modal:
 * Label / Full name · Company / Street address / Apt / City · State · ZIP /
 * Phone / "Set as default" — Cancel · Save address).
 *
 * Persistence is REAL: the collected fields are handed to `onSave`
 * (OrderSummaryRail → CartPage), which `POST`s to `/account/addresses` and
 * refetches the cart (`GET /cart`) so the new address appears in the ship-to
 * list and shows selected — the same address book the checkout step writes to,
 * so it flows through to a placed order via checkout. (It deliberately does NOT
 * `PUT /cart/ship-to`: the cart's ship-to id space is a separate seeded list
 * that 422s a freshly-created account-address id.) `Phone` is collected for
 * design fidelity but has no field on the address contract, so it is not sent.
 */
export function AddAddressModal({ open, onClose, onSave }: AddAddressModalProps) {
  const [label, setLabel] = useState('')
  const [fullName, setFullName] = useState('')
  const [company, setCompany] = useState('')
  const [street, setStreet] = useState('')
  const [apt, setApt] = useState('')
  const [city, setCity] = useState('')
  const [region, setRegion] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [phone, setPhone] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [error, setError] = useState<string>()
  const [submitting, setSubmitting] = useState(false)

  const labelId = useId()
  const fullNameId = useId()
  const companyId = useId()
  const streetId = useId()
  const aptId = useId()
  const cityId = useId()
  const regionId = useId()
  const postalId = useId()
  const phoneId = useId()
  const errorId = useId()

  function reset() {
    setLabel('')
    setFullName('')
    setCompany('')
    setStreet('')
    setApt('')
    setCity('')
    setRegion('')
    setPostalCode('')
    setPhone('')
    setIsDefault(false)
    setError(undefined)
  }

  function handleClose() {
    reset()
    onClose()
  }

  async function handleSave() {
    if (!label.trim() || !street.trim() || !city.trim() || !region.trim() || !postalCode.trim()) {
      setError('Fill in the label, street address, city, state, and ZIP.')
      return
    }
    setSubmitting(true)
    try {
      await onSave({
        label,
        contactName: fullName,
        companyName: company,
        line1: street,
        line2: apt,
        city,
        region,
        postalCode,
        isDefault,
      })
      // Success: the caller closes the modal; reset for the next open.
      reset()
    } catch {
      setError('Could not save that address. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Add a new shipping address"
      maxWidth={560}
      // The design's modal has no visible title — just the ✕ at top-right. An
      // EMPTY in-flow spacer (not an sr-only/out-of-flow node) keeps the
      // header's `justify-content: space-between` pushing the close button to
      // the right; the dialog's accessible name still comes from `title`
      // (Modal applies it as aria-label whenever headerContent is set).
      headerContent={<span aria-hidden="true" />}
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void handleSave()} loading={submitting}>
            Save address
          </Button>
        </>
      }
    >
      <form
        className="add-address-form"
        onSubmit={(event) => {
          event.preventDefault()
          void handleSave()
        }}
      >
        <div className="form-field">
          <label htmlFor={labelId}>Label</label>
          <input
            id={labelId}
            type="text"
            className="input-control-lg"
            placeholder="e.g. HQ, machine shop"
            value={label}
            aria-invalid={error && !label.trim() ? true : undefined}
            aria-describedby={error && !label.trim() ? errorId : undefined}
            onChange={(event) => {
              setLabel(event.target.value)
              if (error) setError(undefined)
            }}
          />
        </div>

        <div className="add-address-row">
          <div className="form-field">
            <label htmlFor={fullNameId}>Full name</label>
            <input
              id={fullNameId}
              type="text"
              className="input-control-lg"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
            />
          </div>
          <div className="form-field">
            <label htmlFor={companyId}>Company</label>
            <input
              id={companyId}
              type="text"
              className="input-control-lg"
              value={company}
              onChange={(event) => setCompany(event.target.value)}
            />
          </div>
        </div>

        <div className="form-field">
          <label htmlFor={streetId}>Street address</label>
          <input
            id={streetId}
            type="text"
            className="input-control-lg"
            value={street}
            aria-invalid={error && !street.trim() ? true : undefined}
            aria-describedby={error && !street.trim() ? errorId : undefined}
            onChange={(event) => {
              setStreet(event.target.value)
              if (error) setError(undefined)
            }}
          />
        </div>

        <div className="form-field">
          <label htmlFor={aptId}>Apt, suite, etc. (optional)</label>
          <input
            id={aptId}
            type="text"
            className="input-control-lg"
            value={apt}
            onChange={(event) => setApt(event.target.value)}
          />
        </div>

        <div className="add-address-row add-address-row-3">
          <div className="form-field">
            <label htmlFor={cityId}>City</label>
            <input
              id={cityId}
              type="text"
              className="input-control-lg"
              value={city}
              aria-invalid={error && !city.trim() ? true : undefined}
              aria-describedby={error && !city.trim() ? errorId : undefined}
              onChange={(event) => {
                setCity(event.target.value)
                if (error) setError(undefined)
              }}
            />
          </div>
          <div className="form-field">
            <label htmlFor={regionId}>State</label>
            <input
              id={regionId}
              type="text"
              className="input-control-lg"
              value={region}
              aria-invalid={error && !region.trim() ? true : undefined}
              aria-describedby={error && !region.trim() ? errorId : undefined}
              onChange={(event) => {
                setRegion(event.target.value)
                if (error) setError(undefined)
              }}
            />
          </div>
          <div className="form-field">
            <label htmlFor={postalId}>ZIP</label>
            <input
              id={postalId}
              type="text"
              className="input-control-lg"
              value={postalCode}
              aria-invalid={error && !postalCode.trim() ? true : undefined}
              aria-describedby={error && !postalCode.trim() ? errorId : undefined}
              onChange={(event) => {
                setPostalCode(event.target.value)
                if (error) setError(undefined)
              }}
            />
          </div>
        </div>

        <div className="form-field">
          <label htmlFor={phoneId}>Phone</label>
          <input
            id={phoneId}
            type="tel"
            className="input-control-lg"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </div>

        <label className="add-address-default">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(event) => setIsDefault(event.target.checked)}
          />
          Set as default shipping address
        </label>

        {error && (
          <p id={errorId} role="alert" className="form-error-text">
            {error}
          </p>
        )}
      </form>
    </Modal>
  )
}
